import { Button } from '@thanx/uikit/button'
import { useMemo, useState } from 'react'

import { locationsApi } from 'api/locations'
import SelectionVersionsModal from 'components/AI/CanonicalPicker/SelectionVersionsModal'
import { buildTranslate } from 'locales'
import type { CanonicalLevel } from 'slices/aiSegmentBuilder/picker'

import useSelectionVersions from './useSelectionVersions'

const t = buildTranslate('components.ai.picker.canonical.versions')

type Props = {
  pickerId: number
  level: CanonicalLevel
  disabled: boolean
}

/** "See what's inside" under a level's picks, and the modal it opens */
const SelectionVersionsContainer = ({ pickerId, level, disabled }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isAvailable, isSpecific, versions, isError, retry } =
    useSelectionVersions(pickerId, level, isOpen)
  const {
    data: locationsData,
    isLoading: isLoadingNames,
    isFetching: isFetchingNames,
    isError: namesFailed,
    refetch: refetchNames,
  } = locationsApi.useGetLocationsQuery({}, { skip: !isOpen })
  const locationNames = useMemo(
    () =>
      new Map(
        (locationsData?.locations ?? []).map(location => [
          location.id,
          location.name,
        ])
      ),
    [locationsData]
  )

  if (!isAvailable) return null

  return (
    <>
      <Button
        kind="minimal"
        size="small"
        className="p-0 mt-xs mr-m"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
      >
        {t('link')}
      </Button>
      <SelectionVersionsModal
        isOpen={isOpen}
        isSpecific={isSpecific}
        versions={isLoadingNames ? null : versions}
        isError={isError}
        locationNames={locationNames}
        locationsFailed={namesFailed}
        isRetryingLocations={isFetchingNames}
        onRetry={retry}
        onRetryLocations={refetchNames}
        onClose={() => setIsOpen(false)}
      />
    </>
  )
}

export default SelectionVersionsContainer
